import { useRef } from 'react'
import { useRouter } from 'next/router'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { m, useInView } from 'framer-motion'
import {
    PageHead,
    ProjectFirstView,
    Gallery,
    Section,
    Description,
    Credit,
    GoBackButton
} from '@/components'
import { getTranslatedField, getDescription, getImage } from '@/libs/utils'
import { getArtworks, getSingleArtwork } from '@/libs/microcms'
import type { GetStaticPropsContext } from 'next'
import type { ArtworkType } from '@/@types/microcms-type'

export default function SingleArtwork({ project }: { project: ArtworkType }) {
    const { locale } = useRouter()

    const sectionRef = useRef(null)
    const goBackRef = useRef(null)
    const isSectionInView = useInView(sectionRef)
    const isGoBackInView = useInView(goBackRef)

    if (!project) return <>no data</>

    const {
        project_title,
        thumbnail_image,
        description,
        gallery,
        gallery_private,
        credit_table,
        meta,
        id
    } = project

    // use gallery_private on the private site, otherwise use gallery
    const gallery_field = gallery_private ?? gallery

    return (
        <>
            <PageHead
                title={getTranslatedField({ field: project_title, locale })}
                description={getDescription({ meta, description, locale })}
                image={getImage({ meta, thumbnail_image })}
            />
            <Section as="div" display="block" width="full">
                <Section
                    as="div"
                    width="full"
                    id="content"
                    className="gap-32 lg:gap-40 bg-v-white pt-40 pb-40 md:pb-40"
                >
                    <ProjectFirstView project={project} locale={locale} />
                    <Description project={project} locale={locale} />

                    {gallery_field.length > 0 && (
                        <Section
                            ref={sectionRef}
                            width="wide"
                            className="bg-v-white lg:max-w-[80vw] gap-y-6 md:gap-y-12 lg:gap-y-20"
                        >
                            <Gallery gallery={gallery_field} />
                        </Section>
                    )}

                    {credit_table && credit_table.length > 0 && (
                        <Credit credit_table={credit_table} />
                    )}
                </Section>

                <m.p
                    key={`${project.project_title.project_title_ja}-${id}`}
                    initial="hidden"
                    animate={
                        isSectionInView && !isGoBackInView
                            ? 'visible'
                            : 'hidden'
                    }
                    variants={{
                        hidden: { opacity: 0, x: 0 },
                        visible: { opacity: 1, x: 20 }
                    }}
                    transition={{ duration: 0.3 }}
                    className="fixed bottom-[1em] only-sm:hidden text-v-navy text-[8.5px] tracking-v-md italic max-w-[33vw]"
                >
                    {getTranslatedField({ field: project_title, locale })}
                </m.p>

                <GoBackButton ref={goBackRef} />
            </Section>
        </>
    )
}

export const getStaticPaths = async ({ locales }: { locales: string[] }) => {
    const projects = await getArtworks({
        fields: ['id'],
        limit: 1000
    })
    const paths = projects.contents.map(({ id }) =>
        locales.map((locale) => ({ params: { id }, locale }))
    )
    return { paths: paths.flat(), fallback: true }
}

interface ExtendedGetStaticPropsContext extends GetStaticPropsContext {
    previewData: {
        slug?: string
        draftKey?: string
    }
    locale: string
}

export const getStaticProps = async ({
    params,
    preview,
    previewData,
    locale
}: ExtendedGetStaticPropsContext) => {
    try {
        let project = null

        if (preview) {
            // Fetch draft data if preview mode is enabled
            const slug = previewData?.slug ?? ''
            const draftKey = previewData?.draftKey
            project = await getSingleArtwork(slug, {
                draftKey
            })
        } else {
            project = await getSingleArtwork(params?.id as string)
            if (!project) return { notFound: true }
        }

        // if isPublic is true, remove 'gallery_private' from project data
        // if isPublic is false, remove 'gallery' from project data
        const isPublic = process.env.NEXT_PUBLIC_PRIVATE_OR_PUBLIC !== 'private'
        project = isPublic
            ? { ...project, gallery_private: null }
            : { ...project, gallery: null }

        return {
            props: {
                project,
                ...(await serverSideTranslations(locale, ['common']))
            }
        }
    } catch (error) {
        console.error('Failed to fetch project data:', error)
        return { notFound: true }
    }
}
